import { ref } from 'vue'
import { type FilterDefinition, FilterDefinitionTypeEnum } from '../filters'
import { getModelByName } from '@/models'
import { useModel } from '@/composables/useModel'

/**
 * Composable for loading async filter options
 */
export function useAsyncFilterOptions(baseFilters: Record<string, any> = {}) {
  const isLoading = ref(false)

  /**
   * Loads options for an async select filter
   */
  async function loadAsyncOptions(filter: FilterDefinition): Promise<any[]> {
    if (
      filter.type !== FilterDefinitionTypeEnum.ASYNC_SELECT ||
      !filter.async?.model
    ) {
      return []
    }

    isLoading.value = true

    try {
      const ModelClass = getModelByName(filter.async.model)
      if (!ModelClass) {
        return []
      }

      // Prepare base filters for the model query.
      // A missing allow_filters hands the whole list over, an empty one none of
      // it: the endpoint behind the filter rejects any filter it does not know.
      const allowFilters = filter.async.allow_filters
      const allowedFilters = Array.isArray(allowFilters)
        ? Object.keys(baseFilters).reduce((acc, key) => {
            if (allowFilters.includes(key)) {
              acc[key] = baseFilters[key]
            }
            return acc
          }, {} as Record<string, any>)
        : baseFilters

      // Use the useModel composable to fetch the data
      const { executeQuery } = useModel(ModelClass, {
        baseFilters: allowedFilters,
        includes: filter.async.includes || [],
      })

      const response = await executeQuery({ limit: -1 })

      if (typeof new ModelClass().toFilterOptions === 'function') {
        return new ModelClass().toFilterOptions(response.data)
      }

      return []
    } catch (error) {
      console.error('Error loading async options:', error)
      return []
    } finally {
      isLoading.value = false
    }
  }

  return {
    isLoading,
    loadAsyncOptions
  }
}

